import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Wallet, Sparkles } from 'lucide-react';
import { Bar, BarChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatEUR(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
}

/**
 * Widget: Real closed-loop ROI from CRM (Sellsy + custom) vs Google Ads spend.
 * Appears on SEA Dashboard — only for admins (endpoint is admin-only).
 * Auto-hides if no revenue tracked yet.
 */
export default function CRMRoiWidget({ days = 30 }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/integrations/sellsy/roi?days=${days}`, {
          withCredentials: true,
        });
        setData(data);
      } catch {
        // silent
      }
    };
    load();
  }, [days, user]);

  if (!user || user.role !== 'admin') return null;
  if (!data) return null;
  // Hide if no CRM activity AND no ad spend (nothing to show)
  if (data.revenue === 0 && data.ad_spend === 0) return null;

  const positive = data.net_roi >= 0;
  const delta = data.delta_pct;

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-200 rounded-lg p-5"
      data-testid="crm-roi-widget"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Wallet className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-lg text-neutral-900">
                ROI réel · Closed-loop CRM
              </h3>
              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium uppercase flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5" /> Temps réel
              </span>
            </div>
            <p className="text-xs text-neutral-600 mt-0.5">
              Revenu CRM attribué (Sellsy + webhooks) · {data.won_count} vente{data.won_count > 1 ? 's' : ''} · {days} j.
            </p>
          </div>
        </div>
        {delta !== null && delta !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
            delta >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`} data-testid="crm-roi-delta">
            {delta >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {Math.abs(delta).toFixed(1)}% vs période préc.
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Stat label="Revenu attribué" value={formatEUR(data.revenue)} color="emerald" testId="crm-roi-revenue" />
        <Stat label="Dépense Ads" value={formatEUR(data.ad_spend)} color="neutral" testId="crm-roi-spend" />
        <Stat
          label="ROI net"
          value={formatEUR(data.net_roi)}
          color={positive ? 'emerald' : 'red'}
          bold
          testId="crm-roi-net"
        />
        <div className="col-span-2 md:col-span-1">
          <div className="text-[10px] text-neutral-500 uppercase tracking-wide font-medium mb-1">Évolution</div>
          {data.sparkline && data.sparkline.length > 0 ? (
            <ResponsiveContainer width="100%" height={48}>
              <BarChart data={data.sparkline}>
                <Tooltip
                  contentStyle={{ backgroundColor: '#0A0A0A', border: 'none', borderRadius: 4, color: '#fff', fontSize: 11 }}
                  formatter={(v) => [formatEUR(v), 'Revenu']}
                />
                <Bar dataKey="revenue" fill="#059669" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-neutral-400">—</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color, bold, testId }) {
  const colors = {
    emerald: 'text-emerald-700',
    red: 'text-red-700',
    neutral: 'text-neutral-900',
  };
  return (
    <div data-testid={testId}>
      <div className="text-[10px] text-neutral-500 uppercase tracking-wide font-medium">{label}</div>
      <div className={`${bold ? 'text-2xl font-bold' : 'text-xl font-semibold'} mt-0.5 ${colors[color] || colors.neutral}`}>
        {value}
      </div>
    </div>
  );
}
